import { ApolloError } from '@apollo/client';
import { CATEGORY as category, ACTION as action } from './FriendsRegistry.constants';
import { createTelemetry, createTelemetryObject } from '@shared/core';
import { PageFunctionType } from '@shared/core/analytics/telemetry/types';
import { TransitionalRegistryStatus } from '@graphql/generated';

type PagesForTelemetry = 'addFromFriendsRegistry' | 'scannedFriendsRegistry';

const pageTracking: Record<PagesForTelemetry, PageFunctionType> = {
  addFromFriendsRegistry: () => ({ category, page: 'addFromFriendsRegistry', pagePrefix: 'admin' }),
  scannedFriendsRegistry: (id: string) => ({ category, page: 'scannedFriendsRegistry', pagePrefix: 'admin', extraPageInfo: { registryId: id } })
};

export const createFriendsRegistryTelemetry = createTelemetryObject({
  actions: {
    inputHandler: (fieldIdentifier: string, inputAction: 'onFocus' | 'onChange') => ({
      action: 'FormFieldInteracted',
      category,
      label: fieldIdentifier,
      extraInfo: {
        inputAction
      }
    }),
    addFromFriendsRegistrySubmit: (name: string, eventId: string, registryUrl: string) => ({
      action,
      label: 'Get Started',
      category,
      extraInfo: { name: `affrBanner_submitButton_${name}`, eventId, registryUrl }
    }),
    registryUrlInfo: (name: string, eventId: string) => ({
      action,
      label: 'Registry URL Info',
      category,
      extraInfo: { name: `affrBanner_registryUrlInfo_${name}`, eventId }
    }),
    createTransitionalRegistryError: (name: string, eventId: string, registryUrl: string, apolloError: ApolloError) => ({
      action,
      label: 'Create Registry Error',
      category,
      extraInfo: { name: `affrBanner_create_registry_error_${name}`, eventId, registryUrl, apolloError }
    }),
    closeRegistryScannerDialog: (name: string, registryId: string, status: TransitionalRegistryStatus) => ({
      action,
      label: 'Close',
      category,
      extraInfo: { name, registryId, status }
    }),
    selectGiftsRegistryScannerDialog: (registryId: string) => ({
      action,
      label: 'Select Gifts',
      category,
      extraInfo: { name: 'affrScannerDialog_selectGifts', registryId }
    }),
    seeAllRegistryWidget: (eventId: string) => ({
      action,
      label: 'See All',
      category,
      extraInfo: { name: 'affrWidget_seeAll', eventId }
    }),
    addNewRegistryWidget: (eventId: string) => ({
      action,
      label: 'Add New Registry',
      category,
      extraInfo: { name: 'affrWidget_addNew', eventId }
    }),
    selectRegistryWidget: (eventId: string, registryId: string) => ({
      action,
      label: 'Select Registry',
      category,
      extraInfo: { name: 'affrWidget_selectRegistry', eventId, registryId }
    }),
    registryViewGifts: (eventId: string, registryId: string) => ({
      action,
      label: 'View Gifts',
      category,
      extraInfo: { name: 'affrScannedRegistriesTable_row_viewGifts', eventId, registryId }
    }),
    registryShare: (eventId: string, registryId: string) => ({
      action,
      label: 'Browse Gifts',
      category,
      extraInfo: { name: 'affrScannedRegistriesTable_row_share', eventId, registryId }
    }),
    registryPreview: (eventId: string, registryId: string) => ({
      action,
      label: "View Friend's Registry",
      category,
      extraInfo: { name: 'affrScannedRegistriesTable_row_preview', eventId, registryId }
    }),
    registryDelete: (eventId: string, registryId: string) => ({
      action,
      label: 'Remove Registry',
      category,
      extraInfo: { name: 'affrScannedRegistriesTable_row_delete', eventId, registryId }
    }),
    registryHowItAllWorks: (eventId: string) => ({
      action,
      label: 'How It Works',
      category,
      extraInfo: { name: 'affrHowItWorksSection', eventId }
    }),
    closeDeleteRegistryDialog: (eventId: string, registryId: string) => ({
      action,
      label: 'Cancel',
      category,
      extraInfo: { name: 'affrDeleteDialog_close', eventId, registryId }
    }),
    confirmDeleteRegistryDialog: (eventId: string, registryId: string) => ({
      action,
      label: 'Delete Registry',
      category,
      extraInfo: { name: 'affrDeleteDialog_confirm', eventId, registryId }
    }),
    doneDeleteRegistryDialog: (eventId: string, registryId: string) => ({
      action,
      label: 'Done',
      category,
      extraInfo: { name: 'affrDeleteDialog_done', eventId, registryId }
    }),
    backScannedRegistry: (eventId: string, registryId: string) => ({
      action,
      label: 'Back',
      category,
      extraInfo: { name: 'affrRegistryDetailsPage_back', eventId, registryId }
    }),
    urlScannedRegistry: (eventId: string, registryId: string) => ({
      action,
      label: 'Scanned',
      category,
      extraInfo: { name: 'affrRegistryDetailsPage_links_url', eventId, registryId }
    }),
    toggleSelectAllScannedRegistry: (eventId: string, registryId: string) => ({
      action,
      label: 'Select All',
      category,
      extraInfo: { name: 'affrRegistryDetailsPage_selectAll', eventId, registryId }
    }),
    addItemScannedRegistry: (eventId: string, registryId: string, count: number, totalProductCount: number) => ({
      action,
      label: 'Select All',
      category,
      extraInfo: { name: 'affrRegistryDetailsPage_addSelectedItems', eventId, registryId, count, totalProductCount }
    }),
    toggleItemScannedRegistry: (eventId: string, registryId: string, checked: boolean) => ({
      action,
      label: 'Toggle Item',
      category,
      extraInfo: { name: 'affrRegistryDetailsPage_toggleItem', eventId, registryId, checked }
    })
  },
  pages: pageTracking
});

const { TelemetryProvider, useTelemetry: useFriendsRegistryTelemetry } = createTelemetry(createFriendsRegistryTelemetry);

export { TelemetryProvider, useFriendsRegistryTelemetry };
